export type GamePhase =
  | 'setup'
  | 'mission_draft'
  | 'round_roll'
  | 'mission_select'
  | 'betting'
  | 'draft'
  | 'settlement'
  | 'final_score'
  | 'game_over';

export type CameraMode = 'overview' | 'table' | 'player' | 'dice';

export type NeighborSide = 'left' | 'right';

export interface Player {
  index: number;
  name: string;
  isHuman: boolean;
  /** Total chip points held */
  chips: number;
  loanTokens: number;
  /** Mission card ids kept after draft (5 cards) */
  missionHand: string[];
  /** Mission card id selected for current round */
  selectedMissionId: string | null;
  /** Chips bet on current mission (remaining after swap costs) */
  betAmount: number;
  /** Personal board slots 1-5 (index 0-4); null = empty */
  board: (number | null)[];
  /** Dice currently in tray */
  tray: number[];
  /** Whether mission swap was used this round */
  missionSwappedThisRound: boolean;
  /** Missions kept face-down during draft */
  draftedMissions: string[];
}

/** Human player's completed round at settlement (for in-game history UI) */
export interface HumanRoundRecord {
  round: number;
  missionId: string;
  betAmount: number;
  success: boolean;
  missionSwapped: boolean;
}

export interface GameState {
  phase: GamePhase;
  playerCount: number;
  players: Player[];
  /** Current round 1-3 */
  round: number;
  /** Draft turn within round 1-4 */
  draftTurn: number;
  /** Player index whose turn it is to act */
  activePlayerIndex: number;
  /** Index of player holding first-player marker tray */
  dealerIndex: number;
  /** How many mission draft picks completed (0-4, then phase ends at 5) */
  missionDraftRound: number;
  /** Temporary hands during mission draft (before finalizing to draftedMissions) */
  missionDraftHands: string[][];
  /** Settlement reveal order cursor */
  settlementIndex: number;
  /** House bank chip points */
  houseChips: number;
  /** Shuffled unused mission ids (not dealt this game) */
  unusedMissionDeck: string[];
  /** RNG seed for reproducibility */
  seed: number;
  /** PRNG state */
  rngState: number;
  /** Final scores after game end */
  finalScores: number[];
  /** Winner player indices (ties allowed) */
  winners: number[];
  /** Human-readable status message */
  message: string;
  /** Human player settlement history for current game */
  humanRoundHistory: HumanRoundRecord[];
}

export type GameAction =
  | { type: 'SETUP'; playerCount: number; seed?: number }
  | { type: 'MISSION_DRAFT_PICK'; playerIndex: number; missionId: string }
  | { type: 'ROLL_DICE' }
  | { type: 'SELECT_MISSION'; playerIndex: number; missionId: string }
  | { type: 'PLACE_BET'; playerIndex: number; amount: number }
  | {
      type: 'SWAP_MISSION';
      playerIndex: number;
      newMissionId: string;
    }
  | {
      type: 'DRAFT_PLACE';
      playerIndex: number;
      /** Index in tray of die to place on right player's board (turns 1-4) */
      trayIndex: number;
    }
  | {
      type: 'DRAFT_TURN4';
      playerIndex: number;
      /** Die placed on right player's board */
      toRightTrayIndex: number;
      /** Die kept on own slot 5 */
      keepTrayIndex: number;
    }
  | { type: 'SETTLEMENT_REVEAL'; playerIndex: number }
  | { type: 'ADVANCE_PHASE' }
  | { type: 'BOT_ACT' };

export const MIN_PLAYERS = 3;
export const MAX_PLAYERS = 6;
export const ROUNDS = 3;
export const DRAFT_TURNS = 4;
export const BOARD_SLOTS = 5;
export const DICE_PER_PLAYER = 5;
export const STARTING_CHIPS = 10;
export const LOAN_CHIP_AMOUNT = 5;
export const LOAN_PENALTY = 10;
export const MAX_LOANS = 2;
export const HUMAN_PLAYER_INDEX = 0;

/** Player to the left when facing table center */
export function leftOf(index: number, playerCount: number): number {
  return (index + 1) % playerCount;
}

/** Player to the right when facing table center */
export function rightOf(index: number, playerCount: number): number {
  return (index - 1 + playerCount) % playerCount;
}
